<template>
  <div class="shared-calc-list">
    <div class="search-title">
      <span class="fa fa-search" @click="searchSharedCalcs($event)"></span>
      <input-control
        v-model="searchTextSharedCalcsInput"
        type="text"
        :no-title="true"
        name="search_title"
        @keypress="onEnter($event, searchSharedCalcs)"
        :placeholder="$t('column.nm.title') + ', ' + $t('column.nm.nm')"
        class="search-title-text"
      />
    </div>
    <div class="selectors">
      <select v-model="searchSexdstnCodeInput" class="simple-design input-control select-type" name="sex_distinction">
        <option value="">
          {{ $t('calculator.placeholder.sex_distinction') }}
        </option>
        <option v-for="(sexdstnCode, sexdstnCodeIndex) in sexdstnCodes" :key="'sex-distinct-code-' + sexdstnCodeIndex" :value="sexdstnCode.codeId">
          {{ $t('code.nm.' + sexdstnCode.codeId) }}
        </option>
      </select>
      <select v-model="searchAgeCodeInput" class="simple-design input-control select-type" name="age_code">
        <option value="">
          {{ $t('calculator.placeholder.age') }}
        </option>
        <option v-for="(ageCode, ageCodeIndex) in ageCodes" :key="'age-' + ageCodeIndex" :value="ageCode.codeId">
          {{ $t('code.nm.' + ageCode.codeId) }}
        </option>
      </select>
      <input-control
        v-model="searchReasonsForInputControlInput"
        type="multiselect"
        :no-title="true"
        :title="$t('calculator.placeholder.reset_reason')"
        class="simple-design reset-reason-select select-type"
        :options="reasonOptions"
      />
    </div>
    <div class="sorting-area">
      <span :class="{'sort-item': true, 'selected': sharedCalcOrder === 'userNm'}" @click="clickSharedCalcOrder('userNm', $event)">{{ $t('column.nm.nm') }} <span><i :class="'fa fa-play ' + sharedCalcDirection" /></span></span>
      <span :class="{'sort-item': true, 'selected': sharedCalcOrder === 'id'}" @click="clickSharedCalcOrder('id', $event)">{{ $t('calculator.sort.time') }} <span><i :class="'fa fa-play ' + sharedCalcDirection" /></span></span>
      <span :class="{'sort-item': true, 'selected': sharedCalcOrder === 'likeCo'}" @click="clickSharedCalcOrder('likeCo', $event)">{{ $t('calculator.sort.like_co') }} <span><i :class="'fa fa-play ' + sharedCalcDirection" /></span></span>
    </div>
    <div class="shared-calcs-list-container" @scroll="onScrollSharedCalcs">
      <div v-if="sharedCalcsTotal === 0" class="no-data">
        {{ $t('common.text.no_data') }}
      </div>
      <div v-else class="shared-calcs-list-wrapper">
        <div v-for="(sharedCalc, sharedCalcIndex) in sharedCalcs" :key="'shared-calc-' + sharedCalcIndex" class="calc-item shared-calc-item" @click="selectSharedCalc(sharedCalc, $event)">
          <div class="first-line">
            <div class="name">
              <img class="reset-editor-icon person" src="/img/icons/ico_info_g.png" alt="Info">
              {{ $t('column.nm.nm') }} : {{ sharedCalc.userNm }}
            </div>
            <div class="date">
              {{ $options.filters.formatDate(sharedCalc.rgsOperDt).replaceAll('/', '.') }}
            </div>
          </div>
          <div class="second-line">
            <div class="age">
              <div class="dot"></div>
              <div>{{ $t('code.nm.' + sharedCalc.ageCode) }}</div>
            </div>
            <div class="sex-distinction">
              <div class="dot"></div>
              <div>{{ $t('code.nm.' + sharedCalc.sexdstnCode) }}</div>
            </div>
            <div class="reasons-area">
              <div class="dot"></div>
              <div>{{ getReasonsText(sharedCalc) }}</div>
            </div>
          </div>
          <div class="third-line">
            <div class="share-ment">
              {{ sharedCalc.shareMent }}
            </div>
          </div>
        </div>
        <div class="clear" />
        <div v-if="sharedCalcsTotal !== undefined && sharedCalcsTotal > sharedCalcsLoaded" class="shared-calcs-loading calcs-loading">
          <div class="clear" />
          <img src="/img/ajax-loader-white-bg.gif" alt="Loading">
          <strong>{{ $t('common.text.loading') }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    initData: {
      type: Object,
      default: undefined
    },
    initConditions: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      sharedCalcs: undefined,
      sharedCalcsTotal: undefined,
      sharedCalcsLoaded: undefined,
      sharedCalcFirstId: undefined,
      searchTextSharedCalcsInput: '',
      sharedCalcOrder: 'id',
      sharedCalcDirection: 'desc',
      searchSexdstnCodeInput: '',
      searchAgeCodeInput: '',
      searchReasonsForInputControlInput: [],
      reasonOptions: [],
      sexdstnCodes: [],
      ageCodes: []
    }
  },
  watch: {
    searchTextSharedCalcsInput: {
      handler(newVal, oldVal) {
        this.emitConditions()
      },
      deep: true
    },
    sharedCalcOrder: {
      handler(newVal, oldVal) {
        this.emitConditions()
      },
      deep: true
    },
    sharedCalcDirection: {
      handler(newVal, oldVal) {
        this.emitConditions()
      },
      deep: true
    },
    searchSexdstnCodeInput: {
      handler(newVal, oldVal) {
        this.emitConditions()
      },
      deep: true
    },
    searchAgeCodeInput: {
      handler(newVal, oldVal) {
        this.emitConditions()
      },
      deep: true
    },
    searchReasonsForInputControlInput: {
      handler(newVal, oldVal) {
        this.emitConditions()
      },
      deep: true
    }
  },
  async mounted() {
    this.ageCodes = await this.getCodes('age')
    this.sexdstnCodes = await this.getCodes('sex_distinction')
    this.resetReasonCodes = await this.getCodes('reset_reason')
    for (let i = 0; i < this.resetReasonCodes.length; i++) {
      const code = this.resetReasonCodes[i]
      this.reasonOptions.push({
        value: code.codeId,
        text: this.$t('code.nm.' + code.codeId)
      })
    }
    if (this.initConditions) {
      for (const key in this.initConditions) {
        this[key] = this.initConditions[key]
      }
      if (this.initData) {
        this.initializeWithData()
        return
      }
      this.searchSharedCalcs()
    } else {
      if (this.initData) {
        this.initializeWithData()
        return
      }
      this.loadSharedCalcs(true)
    }
  },
  methods: {
    emitConditions() {
      this.$emit('condition-changed', {
        searchTextSharedCalcsInput: this.searchTextSharedCalcsInput,
        sharedCalcOrder: this.sharedCalcOrder,
        sharedCalcDirection: this.sharedCalcDirection,
        searchSexdstnCodeInput: this.searchSexdstnCodeInput,
        searchAgeCodeInput: this.searchAgeCodeInput,
        searchReasonsForInputControlInput: this
          .searchReasonsForInputControlInput
      })
    },
    getReasonsText(calc) {
      if (!calc || !calc.reasons) {
        return ''
      }
      let ret = ''
      for (let i = 0; i < calc.reasons.length; i++) {
        if (i > 0) {
          ret += ', '
        }
        ret += this.$t('code.nm.' + calc.reasons[i].resetResnCode)
      }
      return ret
    },
    selectSharedCalc(calc, e) {
      this.$emit('select-calc', {
        calc: calc,
        e: e
      })
    },
    clickSharedCalcOrder(order, e) {
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      this.vibrate(e)
      if (order === this.sharedCalcOrder) {
        if (this.sharedCalcDirection === 'asc') {
          this.sharedCalcDirection = 'desc'
        } else {
          this.sharedCalcDirection = 'asc'
        }
      } else {
        this.sharedCalcDirection = 'desc'
        this.sharedCalcOrder = order
      }
      this.loadSharedCalcs(true)
    },
    searchSharedCalcs(e) {
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      this.vibrate(e)
      this.searchTextSharedCalcs = this.searchTextSharedCalcsInput
      this.searchSexdstnCode = this.searchSexdstnCodeInput
      this.searchAgeCode = this.searchAgeCodeInput
      this.searchReasonsForInputControl = this.searchReasonsForInputControlInput
      this.loadSharedCalcs(true)
    },
    async loadSharedCalcs(refresh) {
      if (refresh) {
        this.sharedCalcs = undefined
        this.sharedCalcFirstId = undefined
        this.sharedCalcsTotal = undefined
        this.sharedCalcsLoaded = 0
        window.sharedCalcsPage = -1
      }
      if (this.sharedCalcsTotal === this.sharedCalcsLoaded) {
        return
      }
      if (this.loadingCalcs) {
        return
      }
      this.loadingCalcs = true
      window.sharedCalcsPage++
      let url = '/calc/calcs?shareFlag=Y&pageNo=' + window.sharedCalcsPage
      if (this.sharedCalcFirstId) {
        url += '&firstId=' + this.sharedCalcFirstId
      }
      if (this.searchTextSharedCalcs) {
        url += '&searchText=' + encodeURIComponent(this.searchTextSharedCalcs)
      }
      if (this.searchSexdstnCode) {
        url += '&searchSexdstnCode=' + this.searchSexdstnCode
      }
      if (this.searchAgeCode) {
        url += '&searchAgeCode=' + this.searchAgeCode
      }
      if (
        this.searchReasonsForInputControl &&
        this.searchReasonsForInputControl.length > 0
      ) {
        for (let i = 0; i < this.searchReasonsForInputControl.length; i++) {
          const value = this.searchReasonsForInputControl[i].value
          url += '&searchReasons=' + encodeURIComponent(value)
        }
      }
      url +=
        '&order=' +
        encodeURIComponent(this.sharedCalcOrder) +
        '&direction=' +
        this.sharedCalcDirection
      const res = await this.$axios.$get(url)
      if (!this.sharedCalcs) {
        this.sharedCalcs = res.data.list
      } else {
        this.sharedCalcs = this.sharedCalcs.concat(res.data.list)
      }
      this.sharedCalcsTotal = res.data.totalSize
      if (!this.sharedCalcFirstId) {
        this.sharedCalcFirstId = res.data.firstId
      }
      if (this.sharedCalcs) {
        this.sharedCalcsLoaded = this.sharedCalcs.length
      }
      this.$nextTick(() => {
        this.loadingCalcs = false
        this.onScrollSharedCalcs()
      })
    },
    emitScrollAndData() {
      this.$emit('scroll-and-data', {
        list: this.sharedCalcs,
        total: this.sharedCalcsTotal,
        firstId: this.sharedCalcFirstId,
        loaded: this.sharedCalcsLoaded,
        scrolledY: this.scrolledY
      })
    },
    initializeWithData() {
      this.sharedCalcs = this.initData.list
      this.sharedCalcsTotal = this.initData.total
      this.sharedCalcFirstId = this.initData.firstId
      this.sharedCalcsLoaded = this.initData.loaded
      this.$nextTick(() => {
        $(this.$el)
          .find('.shared-calcs-list-container')
          .scrollTop(this.initData.scrolledY)
      })
    },
    onScrollSharedCalcs(e) {
      const comp =
        $('.shared-calcs-list-wrapper').height() -
        $('.shared-calcs-list-container').height()
      const top = $('.shared-calcs-list-container').scrollTop()
      if (comp - top < 50) {
        this.loadSharedCalcs(false)
      }
      this.scrolledY = top
      this.emitScrollAndData()
    }
  }
}
</script>

<style scoped>
.shared-calcs-list-container {
  height: calc(100vh - 22.5rem);
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 4rem;
}
.search-title .fa {
  color: #360ea6;
  font-size: 1.5rem;
  margin-left: 1.708333333333333rem;
  position: absolute;
  z-index: 4;
}
.search-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 3.708333333333333rem;
  background: #f1f1f1;
}
.search-title {
  padding: 1rem 0;
}
.search-title >>> input[type='text']::placeholder,
.search-title >>> input[type='text'] {
  font-size: 0.95rem !important;
}
.search-title >>> input[type='text'] {
  padding: 0 0.7rem;
  padding-left: 2.6rem;
  height: 2.291666666666667rem;
}
.search-title-text {
  height: 2.291666666666667rem;
  border: none;
  margin-left: 1rem;
  width: calc(100vw - 2rem);
}
.search-title-text >>> input {
  border: none !important;
  box-shadow: none !important;
}
.sorting-area .fa-play {
  transform: rotate(270deg);
}
.sorting-area .selected .fa-play.desc {
  transform: rotate(90deg);
}
.sorting-area {
  color: #e5e5e5;
  font-size: 0.825rem;
  margin-bottom: 1rem;
  padding: 0 1.1666666rem;
}
.sorting-area .selected {
  color: #360ea6;
}
.sort-item {
  margin-right: 1.5rem;
}
.sort-item .fa {
  margin-left: 0.5rem;
}
.calc-item .title {
  font-weight: lighter;
  overflow: hidden;
  line-height: 1.2rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.calc-item .title .title-content {
  max-width: 5.5rem;
  max-height: 1rem;
  overflow: hidden;
  word-break: keep-all;
  text-overflow: ellipsis;
  text-align: center;

  white-space: nowrap;
  display: block;
  -webkit-line-clamp: 1; /* ellipsis line */
  -webkit-box-orient: vertical;
  margin-top: 0.4rem;

  /* webkit 엔진을 사용하지 않는 브라우저를 위한 속성. */
  /* height = line-height * line = 1.2em * 3 = 3.6em  */
  line-height: 1rem;
  font-size: 1rem;
}
.title .title-container {
  height: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.title .age-container {
  height: 50%;
  width: 100%;
  background: rgba(37, 89, 170, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.calc-item .name,
.calc-item .title {
  font-size: 0.95rem;
}
.calc-item .registration-operation-date {
  font-size: 0.7916rem;
  width: 9.3rem;
}
.calc-item .name-and-title {
  width: calc(100% - 9.3rem);
}
.calcs-loading {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}
>>> .input-control {
  min-height: auto;
  margin-bottom: 0;
}
.no-data {
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 10rem;
}

.selectors {
  margin-bottom: 0.6rem;
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 1.1666666rem;
}
.selectors > select {
  margin-right: 0.4rem;
}
.reset-reason-select {
  width: calc(100% - 11.33rem);
  margin-right: 0 !important;
}
.shared-calc-item {
  width: 100%;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.shared-calc-item .first-line .name {
  color: black;
}
.shared-calc-item .first-line .name img {
  height: 1rem;
  margin-right: 0.6rem;
}
.shared-calc-item .first-line {
  background: rgba(244, 244, 244, 0.4);
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
}
.shared-calc-item .first-line .date {
  font-size: 0.791666666666667rem;
}
.shared-calc-item .title {
  width: 6.8583rem;
  height: 6.8583rem;
  border-radius: 3.4291rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: white;
  font-size: 1rem;
}
.shared-calc-item .name {
  font-size: 1rem;
  padding: 0.9rem 0 0.3rem;
}
.shared-calc-item .summary-data {
  font-weight: lighter;
  line-height: 1.1rem;
  text-align: center;
  font-size: 0.95rem;
  color: #999999;
}
.reasons-area {
  max-width: 8rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.sex-distinction {
  border-radius: 1rem;
  padding: 0.2rem;
}
.sex-distinction img {
  width: 1rem;
  background-blend-mode: overlay;
}
.second-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
}
.second-line > div {
  display: flex;
  align-items: center;
  text-align: left;
  width: calc(100% / 3);
}
.second-line .dot {
  width: 0.5rem;
  height: 0.5rem;
  min-width: 0.5rem;
  min-height: 0.5rem;
  border-radius: 50%;
  background: #e4e4e4;
  margin-right: 0.5rem;
}
.third-line {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.third-line .share-ment {
  width: calc(100% - 4rem);
  border: 1px solid #c5c5c5;
  border-radius: 1.2rem;
  text-align: center;
  padding: 0.6rem;
}
</style>
